import { create } from 'zustand';
import { Environment } from '../types/environment';
import { ToolFilterPreset } from '../types/types';

interface ToolsListViewState {
  page: number;
  searchTerm: string;
  statusFilter: 'all' | 'enabled' | 'disabled' | 'missing' | 'in_progress';
  methodFilter: 'all' | 'get' | 'post' | 'put' | 'delete' | 'patch';
  connectionFilter: string[] | null;
  environmentFilter: Environment | 'all';
  sortBy: 'name' | 'updated_at' | 'created_at';
  selectedTags: string[];
  setPage: (page: number) => void;
  setSearchTerm: (term: string) => void;
  setStatusFilter: (filter: 'all' | 'enabled' | 'disabled' | 'missing' | 'in_progress') => void;
  setMethodFilter: (filter: 'all' | 'get' | 'post' | 'put' | 'delete' | 'patch') => void;
  setConnectionFilter: (filter: string[] | null) => void;
  setEnvironmentFilter: (filter: Environment | 'all') => void;
  setSortBy: (sort: 'name' | 'updated_at' | 'created_at') => void;
  setSelectedTags: (tags: string[]) => void;
  activePresetId: string | null;
  setActivePresetId: (id: string | null) => void;
  applyFilters: (filters: ToolFilterPreset['filters'], presetId?: string) => void;
  resetView: () => void;
}

const defaultState = {
  page: 1,
  searchTerm: '',
  statusFilter: 'all' as const,
  methodFilter: 'all' as const,
  connectionFilter: ['all'] as string[],
  environmentFilter: 'all' as const,
  sortBy: 'updated_at' as const,
  selectedTags: [] as string[],
  activePresetId: null as string | null,
};

export const useToolsListViewStore = create<ToolsListViewState>((set) => ({
  ...defaultState,
  setPage: (page) => set({ page }),
  setSearchTerm: (searchTerm) => set({ searchTerm }),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setMethodFilter: (methodFilter) => set({ methodFilter }),
  setConnectionFilter: (connectionFilter) => set({ connectionFilter }),
  setEnvironmentFilter: (environmentFilter) => set({ environmentFilter }),
  setSortBy: (sortBy) => set({ sortBy }),
  setSelectedTags: (selectedTags) => set({ selectedTags }),
  setActivePresetId: (activePresetId) => set({ activePresetId }),
  applyFilters: (f, presetId) => set({
    page: 1,
    activePresetId: presetId ?? null,
    searchTerm: f.searchTerm ?? defaultState.searchTerm,
    statusFilter: (f.statusFilter as ToolsListViewState['statusFilter']) ?? defaultState.statusFilter,
    methodFilter: (f.methodFilter as ToolsListViewState['methodFilter']) ?? defaultState.methodFilter,
    connectionFilter: f.connectionFilter && f.connectionFilter.length > 0 ? f.connectionFilter : defaultState.connectionFilter,
    environmentFilter: (f.environmentFilter as ToolsListViewState['environmentFilter']) ?? defaultState.environmentFilter,
    sortBy: (f.sortBy as ToolsListViewState['sortBy']) ?? defaultState.sortBy,
    selectedTags: f.selectedTags ?? defaultState.selectedTags,
  }),
  resetView: () => set(defaultState),
}));
